/**
 * FindingCard — connected wrapper that wires the pure {@link FindingCardView}
 * to its data/IO: the promote-to-investigation mutation, the shared
 * tool-card-actions hooks, and the graph contribution handlers (add to active
 * viewpoint / open as exploration).
 *
 * Add to object set: additively merges the finding's nodes/edges into the
 * user's currently active viewpoint (transient view, active investigation, or
 * seeds a new transient if none) and registers a contribution chip. Same
 * semantics as the agent tool cards (FindShortestPath, etc.).
 *
 * Save as investigation: promotes the finding to a saved Investigation; after
 * success the view's button morphs into "Show investigation detail".
 */

import type { ToolActionHandlers } from '@/components/agent/tools/_shared/chat-tool-actions'
import { FindingCardView } from '@/components/hypotheses/FindingCardView'
import { useApplyContributionToGraph } from '@/features/agent/tools/useApplyContributionToGraph'
import { useToolCardActions, useToolCardHint } from '@/features/agent/tools/useToolCardActions'
import type { HypothesisFinding } from '@/types/hypothesis-finding'
import { usePromoteFinding } from './hooks/use-hypothesis-findings'

interface Props {
  finding: HypothesisFinding
  caseId: string
  liveBadge?: boolean
  /** 0-based position in the run; shown as an `F{n}` badge to match the
   * report's [F#] citations. */
  index?: number
  /** Flash the card when a report [F#] citation targets it. */
  highlighted?: boolean
}

export function FindingCard({ finding, caseId, liveBadge, index, highlighted }: Props) {
  const promote = usePromoteFinding(caseId)
  const applyToGraph = useApplyContributionToGraph(caseId)
  const subgraph = finding.subgraph

  const handlers: ToolActionHandlers =
    subgraph?.graphView && caseId
      ? (() => {
          const view = subgraph.graphView
          if (!view) return {}
          const label = view.meta.label ?? finding.title
          const explore = () => applyToGraph('explore', view, label)
          return {
            showOnGraph: explore,
            showSeparately: explore,
            addToGraph: () => applyToGraph('add', view, label),
          }
        })()
      : {}

  const findingEntityIds = subgraph?.graphView?.nodes.map(n => n.id) ?? []
  const hint = useToolCardHint(findingEntityIds, handlers)
  const actions = useToolCardActions(findingEntityIds, handlers, { hideHint: true })

  const handlePromote = () =>
    promote.mutate({
      hypothesisId: finding.hypothesisId,
      runId: finding.runId,
      findingId: finding.id,
    })

  return (
    <FindingCardView
      finding={finding}
      caseId={caseId}
      liveBadge={liveBadge}
      index={index}
      highlighted={highlighted}
      isPromoting={promote.isPending}
      onPromote={handlePromote}
      handlers={handlers}
      actions={actions}
      hint={hint}
    />
  )
}
